import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faBacterium, 
    faAngleDown, 
    faAngleRight, 
    faExclamationTriangle,
    faInfo, 
    faInfoCircle,
    faChartBar,
    faVirus,
    faVirusSlash,
    faLungsVirus,
    faExclamation
} from '@fortawesome/free-solid-svg-icons';
import './GutHealthCard.css';

/**
 * Specialized component for rendering Gut Health analysis data
 */
const GutHealthCard = ({ data, animationDelay }) => {
    // Debug logging
    console.log('Gut Health Card received data:', data);
    
    const [expanded, setExpanded] = useState(false);
    const [activeSection, setActiveSection] = useState('diversity'); // 'diversity', 'bacteria', 'diseases'
    
    // Early return if no data
    if (!data) {
        console.error('Gut Health Card received no data');
        return null;
    }
    
    // Get status info to color elements
    const statusColor = data.status?.toLowerCase() === 'warning' ? '#ff9800' : 
                       data.status?.toLowerCase() === 'negative' ? '#f44336' : 
                       data.status?.toLowerCase() === 'positive' ? '#64b5f6' : '#9e9e9e';
    
    // Access the details object safely
    const details = data.details || {};
    
    // Extract diversity scores
    const diversityScores = details.diversity_scores || {};
    const hasDiversityData = Object.keys(diversityScores).length > 0;
    
    // Extract bacteria findings
    const bacteriaFindings = details.key_bacteria_findings || {};
    const lowBacteria = bacteriaFindings.low || [];
    const highBacteria = bacteriaFindings.high || [];
    const hasBacteriaData = lowBacteria.length > 0 || highBacteria.length > 0;
    
    // Extract disease risk assessment (using correct key structure)
    const diseaseRisk = details.disease_risk_assessment || {};
    const highRiskDiseases = diseaseRisk.potential_concern_diseases || [];
    const hasDiseaseData = highRiskDiseases.length > 0;
    
    console.log('Gut Health data availability:', { 
        hasDiversityData, 
        hasBacteriaData, 
        hasDiseaseData
    });
    
    // Calculate summary information
    const getDiversitySummary = () => {
        if (!hasDiversityData) return null;
        
        // Count diversity issues by status
        const diversityIssues = Object.values(diversityScores).filter(score => score.status === 'Low').length;
        if (diversityIssues > 0) {
            return {
                count: diversityIssues,
                text: `${diversityIssues} diversity ${diversityIssues === 1 ? 'metric' : 'metrics'} below optimal range`,
                isWarning: true
            };
        }
        return null;
    };
    
    const getBacteriaSummary = () => {
        if (!hasBacteriaData) return null;
        
        // Both high and low bacterial populations can be issues
        const totalIssues = highBacteria.length + lowBacteria.length;
        if (totalIssues > 0) {
            return {
                count: totalIssues,
                text: `${totalIssues} bacterial ${totalIssues === 1 ? 'imbalance' : 'imbalances'} detected`,
                isWarning: true
            };
        }
        return null;
    };
    
    const getDiseaseSummary = () => {
        if (!hasDiseaseData) return null;
        
        // Count serious disease risks (score >= 0.7)
        const seriousRisks = highRiskDiseases.filter(disease => disease.score >= 0.7).length;
        if (seriousRisks > 0) {
            return {
                count: seriousRisks,
                text: `${seriousRisks} elevated health ${seriousRisks === 1 ? 'risk' : 'risks'}`,
                isWarning: true
            };
        }
        return { 
            count: highRiskDiseases.length,
            text: `${highRiskDiseases.length} moderate health ${highRiskDiseases.length === 1 ? 'risk' : 'risks'}`,
            isWarning: false
        };
    };
    
    // Get all summaries
    const diversitySummary = getDiversitySummary();
    const bacteriaSummary = getBacteriaSummary();
    const diseaseSummary = getDiseaseSummary();
    
    // Collect all summaries for display
    const summaries = [diversitySummary, bacteriaSummary, diseaseSummary].filter(Boolean);
    
    // Build report summary counts for the top summary display
    const getReportCounts = () => {
        // Get diversity, bacteria, and disease risk counts
        const lowDiversityMetrics = Object.values(diversityScores).filter(score => score.status === 'Low').length;
        const totalBacterialImbalances = highBacteria.length + lowBacteria.length;
        
        // Classify disease risks by severity (using score property)
        const highRisks = highRiskDiseases.filter(disease => disease.score >= 0.7).length;
        const moderateRisks = highRiskDiseases.filter(disease => disease.score >= 0.5 && disease.score < 0.7).length;
        const lowRisks = highRiskDiseases.filter(disease => disease.score < 0.5).length;
        
        return {
            lowDiversityMetrics,
            bacterialImbalances: totalBacterialImbalances,
            highRisks,
            moderateRisks,
            lowRisks
        };
    };
    
    const reportCounts = getReportCounts();

    return (
        <div className="s2-gut-card" style={{ animationDelay }}>
            <div className="s2-gut-header" onClick={() => setExpanded(!expanded)}>
                <div className="s2-gut-icon">
                    <FontAwesomeIcon 
                        icon={faBacterium} 
                        className="s2-gut-icon-bacteria"
                    />
                </div>
                <div className="s2-gut-title-container">
                    <div className="s2-gut-title">Gut Microbiome Analysis</div>
                    <div className="s2-gut-status">
                        {data.status === 'Warning' && (
                            <FontAwesomeIcon 
                                icon={faExclamationTriangle} 
                                className="s2-gut-warning-icon"
                                style={{ color: '#ff9800', marginRight: '0.5vh' }}
                            />
                        )}
                        {data.status === 'Negative' && (
                            <FontAwesomeIcon 
                                icon={faExclamationTriangle} 
                                className="s2-gut-warning-icon"
                                style={{ color: '#f44336', marginRight: '0.5vh' }}
                            />
                        )}
                        <span style={{ color: statusColor }}>{data.status}</span>
                    </div>
                    
                    {/* Gut Health Summary Section */}
                    {summaries.length > 0 && (
                        <div className="s2-gut-summary">
                            {summaries.map((summary, index) => (
                                <div 
                                    key={index} 
                                    className={`s2-gut-pill ${summary.isWarning ? 's2-gut-pill-warning' : ''}`}
                                >
                                    {summary.text}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
                <div className="s2-gut-expand-icon">
                    <FontAwesomeIcon 
                        icon={expanded ? faAngleDown : faAngleRight} 
                    />
                </div>
            </div>
            
            {expanded && (
                <div className="s2-gut-content s2-anim-fadeIn">
                    {/* WGS-style Summary Section */}
                    <div className="s2-gut-wgs-summary">
                        <div className="s2-gut-summary-row">
                            <div className="s2-gut-summary-item">
                                <div className="s2-gut-summary-label">Diversity Metrics</div>
                                <div className="s2-gut-summary-value">
                                    <span className={reportCounts.lowDiversityMetrics > 0 ? 's2-gut-negative' : ''}>
                                        {reportCounts.lowDiversityMetrics} low
                                    </span>
                                    {' / '}{Object.keys(diversityScores).length} total
                                </div>
                            </div>
                            
                            <div className="s2-gut-summary-item">
                                <div className="s2-gut-summary-label">Bacterial Imbalances</div>
                                <div className="s2-gut-summary-value">
                                    <span className={reportCounts.bacterialImbalances > 0 ? 's2-gut-negative' : ''}>
                                        {reportCounts.bacterialImbalances}
                                    </span>
                                    {' detected'}
                                </div>
                            </div>
                            
                            <div className="s2-gut-summary-item">
                                <div className="s2-gut-summary-label">Health Risk Assessment</div>
                                <div className="s2-gut-summary-value">
                                    {reportCounts.highRisks > 0 && (
                                        <span className="s2-gut-negative">{reportCounts.highRisks} high</span>
                                    )}
                                    {reportCounts.highRisks > 0 && reportCounts.moderateRisks > 0 && ', '}
                                    {reportCounts.moderateRisks > 0 && (
                                        <span className="s2-gut-warning">{reportCounts.moderateRisks} moderate</span>
                                    )}
                                    {(reportCounts.highRisks > 0 || reportCounts.moderateRisks > 0) && reportCounts.lowRisks > 0 && ', '}
                                    {reportCounts.lowRisks > 0 && (
                                        <span>{reportCounts.lowRisks} low</span>
                                    )}
                                    {reportCounts.highRisks === 0 && reportCounts.moderateRisks === 0 && reportCounts.lowRisks === 0 && (
                                        <span>No risks detected</span>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                    {/* Tabs navigation */}
                    <div className="s2-gut-tabs">
                        <div 
                            className={`s2-gut-tab ${activeSection === 'diversity' ? 's2-gut-tab-active' : ''}`}
                            onClick={() => setActiveSection('diversity')}
                        >
                            <FontAwesomeIcon icon={faChartBar} className="s2-gut-tab-icon" />
                            <span>Diversity</span>
                        </div>
                        <div 
                            className={`s2-gut-tab ${activeSection === 'bacteria' ? 's2-gut-tab-active' : ''}`}
                            onClick={() => setActiveSection('bacteria')}
                        >
                            <FontAwesomeIcon icon={faBacterium} className="s2-gut-tab-icon" />
                            <span>Bacteria</span>
                        </div>
                        <div 
                            className={`s2-gut-tab ${activeSection === 'diseases' ? 's2-gut-tab-active' : ''}`}
                            onClick={() => setActiveSection('diseases')}
                        >
                            <FontAwesomeIcon icon={faLungsVirus} className="s2-gut-tab-icon" />
                            <span>Health Risks</span>
                        </div>
                    </div>
                    
                    {/* Diversity Scores Section */}
                    {activeSection === 'diversity' && hasDiversityData && (
                        <div className="s2-gut-diversity-section">
                            <h3 className="s2-gut-section-title">Microbiome Diversity Metrics</h3>
                            <div className="s2-gut-diversity-grid">
                                {Object.entries(diversityScores).map(([key, value]) => {
                                    const displayName = key
                                        .replace('_', ' ')
                                        .split(' ')
                                        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                                        .join(' ');
                                    
                                    const statusColorMap = {
                                        'Low': '#f44336',
                                        'Normal': '#4caf50',
                                        'High': '#ff9800'
                                    };
                                    
                                    const metricColor = statusColorMap[value.status] || '#9e9e9e';
                                    
                                    return (
                                        <div key={key} className="s2-gut-diversity-metric">
                                            <div className="s2-gut-diversity-metric-name">{displayName}</div>
                                            <div className="s2-gut-diversity-metric-value">{value.score}</div>
                                            <div className="s2-gut-diversity-metric-status" style={{ color: metricColor }}>
                                                {value.status}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                            <div className="s2-gut-diversity-explanation">
                                <p><strong>Richness:</strong> Number of different bacterial species in your gut.</p>
                                <p><strong>Shannon Diversity:</strong> Measures both richness and evenness of bacteria distribution.</p>
                                <p><strong>Pielou Evenness:</strong> How equally distributed different bacteria are in your gut.</p>
                                <p><strong>F/B Ratio:</strong> Balance between Firmicutes and Bacteroidetes phyla.</p>
                            </div>
                        </div>
                    )}
                    
                    {/* Bacteria Findings Section */}
                    {activeSection === 'bacteria' && hasBacteriaData && (
                        <div className="s2-gut-bacteria-section">
                            <h3 className="s2-gut-section-title">Key Bacteria Findings</h3>
                            
                            {highBacteria.length > 0 && (
                                <div className="s2-gut-bacteria-group">
                                    <h4 className="s2-gut-bacteria-group-title">
                                        <FontAwesomeIcon icon={faVirus} className="s2-gut-bacteria-icon" style={{ color: '#4caf50' }} />
                                        Increased Bacteria
                                    </h4>
                                    <div className="s2-gut-bacteria-list">
                                        {highBacteria.map((bacteria, index) => (
                                            <div key={index} className="s2-gut-bacteria-item">
                                                <div className="s2-gut-bacteria-name">{bacteria.name}</div>
                                                <div className="s2-gut-bacteria-type">{bacteria.type}</div>
                                                <div className="s2-gut-bacteria-level" style={{ color: '#4caf50' }}>
                                                    {bacteria.level}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                            
                            {lowBacteria.length > 0 && (
                                <div className="s2-gut-bacteria-group">
                                    <h4 className="s2-gut-bacteria-group-title">
                                        <FontAwesomeIcon icon={faVirusSlash} className="s2-gut-bacteria-icon" style={{ color: '#f44336' }} />
                                        Decreased Bacteria
                                    </h4>
                                    <div className="s2-gut-bacteria-list">
                                        {lowBacteria.map((bacteria, index) => (
                                            <div key={index} className="s2-gut-bacteria-item">
                                                <div className="s2-gut-bacteria-name">{bacteria.name}</div>
                                                <div className="s2-gut-bacteria-type">{bacteria.type}</div>
                                                <div className="s2-gut-bacteria-level" style={{ color: '#f44336' }}>
                                                    {bacteria.level}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                    
                    {/* Disease Risk Section */}
                    {activeSection === 'diseases' && hasDiseaseData && (
                        <div className="s2-gut-disease-section">
                            <h3 className="s2-gut-section-title">Health Risk Assessment</h3>
                            <div className="s2-gut-disease-list">
                                {highRiskDiseases.map((disease, index) => {
                                    // Format the disease name for better display
                                    const formattedName = disease.disease_name
                                        .replace(/\./g, ' - ')
                                        .split(' ')
                                        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
                                        .join(' ');
                                    
                                    // Calculate the risk bar width as percentage
                                    const riskPercentage = Math.round(disease.score * 100);
                                    
                                    // Determine color based on risk level
                                    const riskColor = 
                                        disease.score >= 0.7 ? '#f44336' : // red for high risk
                                        disease.score >= 0.5 ? '#ff9800' : // orange for moderate risk
                                        '#4caf50'; // green for low risk
                                    
                                    return (
                                        <div key={index} className="s2-gut-disease-item">
                                            <div className="s2-gut-disease-header">
                                                <div className="s2-gut-disease-name">
                                                    {disease.score >= 0.7 && (
                                                        <FontAwesomeIcon 
                                                            icon={faExclamation} 
                                                            className="s2-gut-disease-warning-icon" 

                                                        />
                                                    )}
                                                    {formattedName}
                                                </div>
                                                <div className="s2-gut-disease-score" >
                                                    {riskPercentage}%
                                                </div>
                                            </div>
                                            
                                            <div className="s2-gut-disease-risk-bar-container">
                                                <div 
                                                    className="s2-gut-disease-risk-bar" 
                                                    style={{ 
                                                        width: `${riskPercentage}%`,
                                                    }}
                                                ></div>
                                            </div>
                                            
                                            {disease.related_microbes_flagged && disease.related_microbes_flagged.length > 0 && (
                                                <div className="s2-gut-disease-microbes">
                                                    <div className="s2-gut-disease-microbes-toggle" onClick={(e) => {
                                                        e.currentTarget.parentElement.classList.toggle('s2-gut-microbes-expanded');
                                                    }}>
                                                        <FontAwesomeIcon icon={faAngleRight} className="s2-gut-microbes-toggle-icon" />
                                                        <span>Related Microbes ({disease.related_microbes_flagged.length})</span>
                                                    </div>
                                                    <div className="s2-gut-disease-microbes-content">
                                                        <div className="s2-gut-disease-microbes-list">
                                                            {/* Only show first 5 by default */}
                                                            {disease.related_microbes_flagged.slice(0, 5).map((microbe, mIndex) => (
                                                                <div key={mIndex} className="s2-gut-disease-microbe">
                                                                    <div className="s2-gut-disease-microbe-name">{microbe.name}</div>
                                                                    <div className="s2-gut-disease-microbe-role">{microbe.role}</div>
                                                                    <div 
                                                                        className="s2-gut-disease-microbe-level"
                                                                        style={{ 
                                                                            color: microbe.level === 'Increased' ? '#4caf50' : '#f44336' 
                                                                        }}
                                                                    >
                                                                        {microbe.level}
                                                                    </div>
                                                                </div>
                                                            ))}
                                                            
                                                            {disease.related_microbes_flagged.length > 5 && (
                                                                <div className="s2-gut-disease-microbes-more">
                                                                    + {disease.related_microbes_flagged.length - 5} more microbes
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                    
                    {/* Fallback display if no data is available for the selected section */}
                    {activeSection === 'diversity' && !hasDiversityData && (
                        <div className="s2-gut-no-data">No diversity data available</div>
                    )}
                    {activeSection === 'bacteria' && !hasBacteriaData && (
                        <div className="s2-gut-no-data">No bacteria findings available</div>
                    )}
                    {activeSection === 'diseases' && !hasDiseaseData && (
                        <div className="s2-gut-no-data">No disease risk assessment available</div>
                    )}
                </div>
            )}
            
            {data.source && (
                <div className="s2-gut-footer">
                    <span className="s2-gut-source">
                        <FontAwesomeIcon icon={faInfoCircle} className="s2-gut-source-icon" />
                        {data.source}
                    </span>
                </div>
            )}
        </div>
    );
};

export default GutHealthCard;